import { useEffect, useRef } from 'react';
import getConfig from 'next/config';

import 'leaflet/dist/leaflet.css';
import { Map, Marker } from 'leaflet';
import { Leaflet, IPickupPoint, IPositionInMap } from '@monofront/types';
const { publicRuntimeConfig } = getConfig();

interface IPickupMapProps {
  pickupList: IPickupPoint[];
  pickupFocused: string | null;
  setPickupFocused: (pointId: string) => void;
  homePosition?: IPositionInMap;
}

const PickupMap: React.FC<IPickupMapProps> = ({
  pickupList,
  pickupFocused,
  setPickupFocused,
  homePosition,
}) => {
  const markers = useRef<{
    [key: string]: Marker;
  }>({});
  const leaflet = useRef<Leaflet | undefined>(undefined);
  const previouslyFocused = useRef<string | null>(null);
  const pickupMap = useRef<Map | undefined>(undefined);
  const previousPickupListCount = useRef<number | null>(null);

  useEffect(() => {
    if (leaflet.current && pickupFocused) {
      markers.current[pickupFocused].setIcon(
        leaflet.current.divIcon({
          className: 'marker-icon focused',
        }),
      );
      markers.current[pickupFocused].setZIndexOffset(1000);

      if (previouslyFocused.current) {
        markers.current[previouslyFocused.current]?.setIcon(
          leaflet.current.divIcon({
            className: 'marker-icon',
          }),
        );
        markers.current[previouslyFocused.current]?.setZIndexOffset(0);
      }

      const { lat, lng } = markers.current[pickupFocused].getLatLng();

      pickupMap.current?.setView([lat, lng]);
    }

    previouslyFocused.current = pickupFocused;
  }, [pickupFocused]);

  useEffect(() => {
    const pickupListCount = pickupList.length;

    if (
      pickupListCount &&
      pickupListCount !== previousPickupListCount.current
    ) {
      import('leaflet').then((l) => {
        leaflet.current = l;

        if (!pickupMap.current) {
          pickupMap.current = leaflet.current.map('map', {
            zoomSnap: 0.5,
          });

          leaflet.current
            .tileLayer(
              `https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=${publicRuntimeConfig.mapboxToken}`,
              {
                maxZoom: 18,
                id: 'mapbox/streets-v11',
              },
            )
            .addTo(pickupMap.current);

          if (homePosition?.latitude && homePosition?.longitude) {
            const homeMarker = leaflet.current.marker(
              [homePosition?.latitude, homePosition?.longitude],
              {
                icon: leaflet.current!.divIcon({
                  html: `
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                          d="M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z"
                          fill="white" />
                      <path d="M8 14L16 8L24 14H22.6667V23.3333H20.3333H18V18.6667H16H14V23.3333H9.33333V14H8Z" stroke="black"
                          stroke-linecap="round" stroke-linejoin="round" />
                  </svg>`,
                }),
              },
            );

            homeMarker.setZIndexOffset(900);
            homeMarker.addTo(pickupMap.current);
          }
        }

        if (markers.current) {
          Object.values(markers.current).forEach((marker) => marker.remove());
        }

        pickupList.forEach(({ pointId, longitude, latitude }) => {
          const focused = pickupFocused === pointId;
          const marker = leaflet.current!.marker([latitude, longitude], {
            icon: leaflet.current!.divIcon({
              className: `marker-icon${focused ? ' focused' : ''}`,
            }),
          });

          marker.addTo(pickupMap.current!);
          marker.on('click', () => {
            setPickupFocused(pointId);
          });

          markers.current[pointId] = marker;
        });

        // zoom in/out to show all markers
        const markerGroup = leaflet.current?.featureGroup([
          ...Object.values(markers.current),
        ]);

        if (markerGroup) {
          pickupMap.current?.fitBounds(markerGroup?.getBounds(), {
            padding: [20, 20],
          });
        }

        previousPickupListCount.current = pickupListCount;
      });
    }
  }, [pickupList]);

  return (
    <>
      <div id="map" className="map" />
      <style jsx>{`
        .map {
          width: 100%;
          height: 100%;
        }

        :global(.leaflet-div-icon) {
          background: transparent !important;
          border: transparent !important;
        }
      `}</style>
    </>
  );
};

export default PickupMap;
